import { useState } from 'react';
import { iDataTableColumn } from '../components/reusable-components/data-table/DataTable';
import { iTableSort, SortDirection } from '../components/reusable-components/data-table/TableToolbar';
import { iFilterItem } from '../types/filterTypes';
import useTableFilters, { iTableFilterDef } from './useTableFilters';
import usePagingFlash from './usePagingFlash';
import { getSearchTerms, matchesSearchTerms } from '../utils/textSearch';

const SORT_WORDS: Record<NonNullable<iDataTableColumn<unknown>['sortType']>, { asc: string; desc: string }> = {
  text: { asc: 'A → Z', desc: 'Z → A' },
  number: { asc: 'Low → high', desc: 'High → low' },
  date: { asc: 'Oldest first', desc: 'Newest first' },
};

type tSortable = string | number | Date | null | undefined;

const compareValues = (a: tSortable, b: tSortable) => {
  const empty = (value: tSortable) => value === null || value === undefined || value === '';
  if (empty(a) || empty(b)) return empty(a) === empty(b) ? 0 : empty(a) ? 1 : -1;
  if (a instanceof Date || b instanceof Date) return new Date(a as Date).getTime() - new Date(b as Date).getTime();
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const numA = Number(a);
  const numB = Number(b);
  if (!Number.isNaN(numA) && !Number.isNaN(numB) && String(a).trim() && String(b).trim()) return numA - numB;
  return String(a).localeCompare(String(b));
};

/** Pages over rows the server already paged, while no search or filter needs every row. */
export interface iServerPagination {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number, pageSize: number) => void;
}

interface iUseListTableParams<T> {
  rows: T[];
  columns: iDataTableColumn<T>[];
  filterDefs?: iTableFilterDef<T>[];
  initialItems?: iFilterItem[];
  defaultSort?: { key: string; direction: SortDirection };
  defaultPageSize?: number;
  serverPagination?: iServerPagination;
}

const useListTable = <T>({
  rows,
  columns,
  filterDefs = [],
  initialItems,
  defaultSort,
  defaultPageSize = 10,
  serverPagination,
}: iUseListTableParams<T>) => {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(defaultSort?.key ?? '');
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultSort?.direction ?? 'desc');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(defaultPageSize);
  const resetPage = () => setPage(1);

  const terms = getSearchTerms(search);
  const searchable = columns.filter((column) => column.searchValue || column.sortValue);
  const passesSearch = (row: T) =>
    matchesSearchTerms(
      terms,
      searchable.map((column) => (column.searchValue ? column.searchValue(row) : column.sortValue?.(row)))
    );

  const {
    toolbarProps: filterProps,
    items,
    setItems,
    passesFilters,
    hasFilters,
    hasRules,
    hasOpenFilters,
  } = useTableFilters(rows, filterDefs, passesSearch, resetPage, initialItems, {
    value: (row, field) => {
      const raw = (row as Record<string, unknown>)[field];
      if (raw !== undefined) return raw;
      const column = columns.find((item) => item.key === field);
      return column?.sortValue?.(row) ?? column?.searchValue?.(row);
    },
    label: (field) =>
      columns.find((item) => item.key === field)?.label ??
      field
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .toLowerCase()
        .replace(/^./, (first) => first.toUpperCase()),
  });

  const sortColumns = columns.filter((column) => column.sortValue);
  const sorts: iTableSort[] = sortColumns.map((column) => ({
    key: column.key,
    label: column.label,
    ...SORT_WORDS[column.sortType ?? 'text'],
  }));
  const sortColumn = sortColumns.find((column) => column.key === sortKey);

  const narrowed = terms.length > 0 || hasFilters || hasRules || hasOpenFilters;
  const filtered = rows.filter((row) => passesSearch(row) && passesFilters(row));
  const sortValue = sortColumn?.sortValue ?? ((row: T) => (row as { createdAt?: tSortable }).createdAt);
  const sorted = [...filtered].sort(
    (a, b) => (sortDirection === 'desc' ? -1 : 1) * compareValues(sortValue(a), sortValue(b))
  );

  const server = serverPagination && !narrowed ? serverPagination : null;
  const total = server ? server.total : sorted.length;
  const pageSize = server ? server.pageSize : rowsPerPage;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = server ? server.page + 1 : Math.min(page, totalPages);
  const pageRows = server ? sorted : sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pageFlash = usePagingFlash(currentPage, pageSize);

  const pickSort = (key: string) => {
    const column = sortColumns.find((item) => item.key === key);
    setSortKey(key);
    setSortDirection(!column || column.sortType === 'date' ? 'desc' : 'asc');
  };

  const onSort = (key: string) => {
    if (key === sortKey) setSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'));
    else pickSort(key);
  };

  return {
    pageRows,
    rows: sorted,
    items,
    setItems,
    hasFilters,
    /** True while a search or filter is applied — a server-paged list then needs every row. */
    narrowed,
    paging: !server && pageFlash,
    toolbarProps: {
      search,
      onSearch: (value: string) => {
        setSearch(value);
        resetPage();
      },
      ...filterProps,
      sorts,
      sortKey: sortColumn ? sortKey : '',
      sortDirection,
      onSortKey: pickSort,
      onSortDirection: setSortDirection,
    },
    tableProps: { sortKey, sortDirection, onSort },
    footerProps: {
      page: currentPage,
      totalPages,
      rowsPerPage: pageSize,
      total,
      onPageChange: (next: number) => (server ? server.onChange(next - 1, server.pageSize) : setPage(next)),
      onRowsPerPageChange: (value: number) => {
        if (server) server.onChange(0, value);
        else {
          setRowsPerPage(value);
          resetPage();
        }
      },
    },
  };
};

export default useListTable;
